import { useEffect, useRef } from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { BASE_URL } from "../api";

export default function ChatBot({
  analyst,
  sessionId,
  chatMessage,
  setChatMessage,
  isLoading,
  isRecording,
  isTalkToAgent,
  startRecording,
  stopRecording,
  handleSendMessage,
  onResponse,
  onUserSend,
  includeAudio = true,
  voiceGender = "female",
  languageCode = "en-US",
  setIsLoading,
  useWebSocketVoice = true,
}) {
  const wsRef = useRef(null);
  const audioChunksRef = useRef([]);
  const readyForAudioRef = useRef(false);
  const streamingRecorderRef = useRef(null);
  const wsConnectedRef = useRef(false);
  const lastSentQuestionRef = useRef("");
  const lastResponseIdRef = useRef(null);


  // compute WS base from REST BASE_URL
  function getWsUrl(sessionId) {
    // e.g., https://host/api/v1 -> wss://host/api/v1
    // api/v1/ws/query/{session_id}?token=<jwt></jwt>
    const wsBase = BASE_URL.replace(/^http:/, "ws:").replace(/^https:/, "wss:");
    const accessToken = localStorage.getItem('access_token');

    // Add token as query parameter for WebSocket authentication
    let wsUrl = `${wsBase}/ws/query/${sessionId}`;
    if (accessToken) {
      wsUrl += `?token=${encodeURIComponent(accessToken)}`;
    }

    return wsUrl;
  }

  // === WebSocket setup ===
  useEffect(() => {
    if (!sessionId) return;
    let ws;
    let retryTimer;

    const connect = () => {
      const url = getWsUrl(sessionId);
      ws = new WebSocket(url);
      ws.binaryType = "arraybuffer";

      ws.onopen = () => {
        wsConnectedRef.current = true;
        console.info('[ChatBot] WS connected');
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          if (message?.type === "connected" || message?.type === "info") {
            // optional: could toast/log
          }
          handleStreamMessage(message);
        } catch (e) {
          console.warn('[ChatBot] WS received non-JSON or error parsing:', event.data, e);
          // ignore non-JSON/binary frames
        }
      };

      ws.onclose = (event) => {
        wsConnectedRef.current = false;
        console.warn('[ChatBot] WS closed:', event.code, event.reason);
        // basic retry with small delay
        retryTimer = setTimeout(connect, 1500);
      };

      ws.onerror = (error) => {
        wsConnectedRef.current = false;
        console.error('[ChatBot] WS error:', error);
      };

      wsRef.current = ws;
    };

    connect();

    return () => {
      if (retryTimer) clearTimeout(retryTimer);
      if (ws && ws.readyState === WebSocket.OPEN) ws.close(1000, "Component unmounted");
    };
  }, [sessionId]);

  // === Handle WS stream events ===
  function handleStreamMessage(message) {
    const { type } = message;

    switch (type) {
      case "ready_for_audio": {
        // Allow MediaRecorder data chunks to be sent
        readyForAudioRef.current = true;
        break;
      }
      case "text_start": {
        // Start streaming without emitting an empty message; first chunk will create/update
        lastResponseIdRef.current = Date.now();
        break;
      }

      case "text_chunk": {
        const content = message.content || "";
        if (!content) return;
        onResponse?.((prev) => ({
          ...prev,
          id: lastResponseIdRef.current,
          type: "ai",
          question: prev?.question || lastSentQuestionRef.current || "",
          answer: (prev?.answer || "") + content,
          streaming: true,
        }));
        break;
      }

      case "text_complete": {
        console.log("[ChatBot] text_complete received:", message);
          const allSources = Array.isArray(message?.sources) ? message.sources : [];
          const usedSources = Array.isArray(message?.used_sources) ? message.used_sources : [];
          const key = (s) => `${s?.file || ""}|${Array.isArray(s?.pages) ? s.pages.join(",") : s?.pages ?? ""}`;
          const usedKeys = new Set(usedSources.map(key));
          const additionalSources = allSources.filter((s) => !usedKeys.has(key(s)));

        // Update the visible AI message with final text + sources
        onResponse?.((prev) => ({
            ...prev,
            id: lastResponseIdRef.current,
            answer: message.full_text || prev?.answer || "",
            sources: allSources,
            used_sources: usedSources,
            additional_sources: additionalSources,
            streaming: false,
        }));

        // Also emit a lightweight meta event so parent can trigger avatar speech
        console.log("[ChatBot] emitting meta text_complete for avatar speech");
        onResponse?.({
          id: lastResponseIdRef.current,
          type: "text_complete",
          answer: message.full_text || "",
          sources: allSources,
          used_sources: usedSources,
          additional_sources: additionalSources,
          streaming: false,
        });

        // Ensure global loading is cleared when text finishes
        try { setIsLoading?.(false); } catch { }
        break;
      }

      case "audio_chunk":
        if (message.data) {
          let chunk = message.data.trim();
          const prefixIndex = chunk.indexOf(",");
          if (chunk.startsWith("data:") && prefixIndex > -1) {
            chunk = chunk.slice(prefixIndex + 1);
          }
          audioChunksRef.current.push(chunk);
        }
        break;

      case "audio_complete": {
        let combined = audioChunksRef.current.filter(Boolean).join("");
        audioChunksRef.current = [];

        const sanitizeBase64 = (input) => {
          let b64 = (input || "").replace(/[^A-Za-z0-9+/=_-]/g, ""); // strip junk
          b64 = b64.replace(/-/g, "+").replace(/_/g, "/");
          while (b64.length % 4) b64 += "=";
          return b64;
        };

        const cleanedBase64 = sanitizeBase64(combined);
        if (!cleanedBase64 || cleanedBase64.length < 500) {
          console.warn("[Audio] Ignoring empty or invalid audio");
          return;
        }

        onResponse((prev) => ({
          ...(typeof prev === "object" ? prev : {}),
          type: "audio_complete",
          audio: cleanedBase64,
          audioMime: "auto",
        }));
        // Clear loading once audio is finalized
        try { setIsLoading?.(false); } catch { }
        break;
      }


      case "chat_complete":
        // Signal that chat is complete - this will be handled by parent component
        onResponse?.({
          id: lastResponseIdRef.current || Date.now(),
          type: "chat_complete",
          streaming: false,
        });
        setIsLoading(false);
        break;

      case "error":
        onResponse?.({
          id: Date.now(),
          type: "ai",
          question: lastSentQuestionRef.current || "",
          answer: `⚠️ ${message.message || "Streaming error"}`,
          streaming: false,
        });
        break;

      default:
        break;
    }
  }




  // === Send chat over WebSocket ===
  function sendChatOverWS() {
    setIsLoading(true);
    const ws = wsRef.current;
    const trimmed = (chatMessage || "").trim();
    if (!ws || ws.readyState !== WebSocket.OPEN || !trimmed) return false;

    lastSentQuestionRef.current = trimmed;

    const payload = {
      action: "chat",
      question: trimmed,
      include_audio: !!includeAudio,
      voice_gender: voiceGender || "female",
    };

    if (typeof onUserSend === "function") onUserSend(trimmed);
    ws.send(JSON.stringify(payload));
    return true;
  }


  // === Helper: check socket state ===
  function isSocketReady() {
    const ws = wsRef.current;
    const state = ws ? ws.readyState : -1;
    const open = ws && state === WebSocket.OPEN && wsConnectedRef.current;
    try { console.debug('[ChatBot] WS ready?', { open, readyState: state }); } catch { }
    return !!open;
  }

  // === Voice streaming over WebSocket ===
  async function startStreamingVoiceWS() {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN || !wsConnectedRef.current) return false;
    readyForAudioRef.current = false;
    // control message
    const ctrl = {
      action: "voice_chat",
      include_audio: !!includeAudio,
      voice_gender: voiceGender || "female",
      language_code: languageCode || "en-US",
      audio_format: "audio/webm",
    };
    try {
      ws.send(JSON.stringify(ctrl));
    } catch {
      return false;
    }

    // start recording
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm';
      const recorder = new MediaRecorder(stream, { mimeType: mime });
      streamingRecorderRef.current = recorder;

      recorder.ondataavailable = async (evt) => {
        // ignore tiny/empty chunks that often represent silence
        if (evt.data && evt.data.size > 1024) {
          // wait until server says ready_for_audio
          if (!readyForAudioRef.current) return;
          const buf = await evt.data.arrayBuffer();
          try {
            ws.send(buf);
          } catch { }
        }
      };
      recorder.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
      };
      // slightly larger timeslice improves STT stability on some servers
      recorder.start(500);
      return true;
    } catch {
      return false;
    }
  }

  function stopStreamingVoiceWS() {
    const recorder = streamingRecorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.stop();
    }
    streamingRecorderRef.current = null;
    readyForAudioRef.current = false;
  }

  // === Text send helper (WS first, HTTP fallback) ===
  async function handleTextSend() {
    const trimmed = (chatMessage || "").trim();
    if (!trimmed) return;
    // Prefer WebSocket when socket is connected and ready
    if (isSocketReady()) {
      const ok = sendChatOverWS();
      if (!ok) {
        try { console.warn('[ChatBot] WS send failed'); } catch { }
      }
      return;
    }
  }

  // === UI ===
  return (
    <div className="sticky bottom-0 bg-white/95 backdrop-blur-xl border-t border-gray-200/50 p-5 shadow-2xl">
      <div className="max-w-4xl mx-auto">
        {isTalkToAgent ? (
          // === TALK MODE ===
          <div className="flex flex-col items-center justify-center space-y-3">
            <div className="relative group">
              <div
                className={`absolute inset-0 rounded-full blur-2xl opacity-60 transition-all duration-300 ${isRecording
                  ? "bg-red-500 animate-pulse"
                  : "bg-gradient-to-br from-blue-500 to-purple-600 group-hover:scale-110 group-hover:opacity-80"
                  }`}
              ></div>

              <Button
                icon={isRecording ? "pi pi-stop" : "pi pi-microphone"}
                className={`relative p-button-rounded w-[32px] h-[32px] text-5xl font-bold transition-all duration-300 border-0 ${isRecording
                  ? "bg-red-600 text-white scale-110 animate-pulse shadow-[0_0_40px_rgba(239,68,68,0.8)]"
                  : "bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 text-white hover:scale-110 hover:shadow-[0_0_45px_rgba(147,51,234,0.8)]"
                  }`}
                onClick={isRecording ? stopRecording : startRecording}
                tooltip={isRecording ? "Stop recording" : "Start talking to agent"}
              />
            </div>

            <p
              className={`text-lg font-semibold transition-all duration-300 ${isRecording ? "text-red-600 animate-pulse" : "text-gray-600"
                }`}
            >
              {isRecording ? "Listening..." : "Tap to Talk"}
            </p>
          </div>
        ) : (
          // === CHAT MODE ===
          <div className="flex items-center gap-3 bg-gray-50 p-2 rounded-2xl focus-within:border-blue-400 focus-within:bg-white transition-all duration-200">
            <InputText
              value={chatMessage}
              onChange={(e) => setChatMessage(e.target.value)}
              placeholder="Ask me"
              className="flex-1 border-0 bg-transparent focus:ring-0 px-3"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleTextSend();
                }
              }}
              disabled={isRecording || isLoading}
            />
            <Button
              icon={isRecording ? "pi pi-stop" : "pi pi-microphone"}
              className={`p-button-rounded w-11 h-11 shadow-md hover:shadow-lg transition-all duration-200 ${isRecording ? "p-button-danger animate-pulse" : "p-button-outlined border-2"
                }`}
              onClick={() => {
                if (isRecording) {
                  stopRecording();
                } else {
                  startRecording();
                }
              }}
              disabled={isLoading}
              tooltip={isRecording ? "Stop recording" : "Record audio message"}
            />
            <Button
              icon="pi pi-send"
              className="p-button-rounded w-11 h-11 bg-gradient-to-br from-blue-500 to-purple-600 border-0 shadow-md hover:shadow-lg hover:scale-105 active:scale-95 transition-all duration-200"
              onClick={handleTextSend}
              disabled={!chatMessage.trim() || isLoading || isRecording}
              loading={isLoading}
            />
          </div>
        )}
      </div>
    </div>
  );
}
